/** @format */

import React, { useState, useEffect } from 'react';
import { Button, Space } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { usePolicies } from '../hooks/usePolicies';
import { useUpdatePolicy } from '../hooks/useUpdatePolicy';
import { useDeletePolicy } from '../hooks/useDeletePolicy';
import PoliciesTable from '../components/PoliciesTable';
import ExportPoliciesButton from '../components/ExportPoliciesButton';
import { message } from 'antd';
import { Policy } from '../services/getPolicies';

interface PoliciesTableContainerProps {
  filter?: Record<string, any>;
}

const PoliciesTableContainer: React.FC<PoliciesTableContainerProps> = ({ filter }) => {
  const { policies, loading, error, refetch } = usePolicies(filter);
  const { updatePolicyData } = useUpdatePolicy();
  const { deletePolicyById } = useDeletePolicy();
  const [filteredPolicies, setFilteredPolicies] = useState<Partial<Policy>[]>([]);

  useEffect(() => {
    // When policies change (initial load or refetch), reset filteredPolicies to all policies
    setFilteredPolicies(policies);
  }, [policies]);

  const handleUpdatePolicy = async (policyId: string, nestedRecord: any, updateData: Record<string, any>) => {
    try {
      // Build the nested update payload based on nestedType, preserving all existing fields
      let payload: Record<string, any> = {};
      const nestedType = nestedRecord.nestedType;
      
      if (nestedType === 'majorMed' || nestedType === 'privateMed') {
        // Merge the update with existing nested object to preserve all fields
        payload[nestedType] = {
          ...nestedRecord,
          ...updateData,
        };
        // Remove the custom tracking fields we added
        delete payload[nestedType].type;
        delete payload[nestedType].parentId;
        delete payload[nestedType].nestedType;
        delete payload[nestedType].ancillaryIndex;
        delete payload[nestedType].__typename;
        // Remove fake _id if it's a generated one (like 'majorMed-xxx' or 'privateMed-xxx')
        if (payload[nestedType]._id && (payload[nestedType]._id.startsWith('majorMed-') || payload[nestedType]._id.startsWith('privateMed-'))) {
          delete payload[nestedType]._id;
        }
      } else if (nestedType === 'ancillaries') {
        // Find the original policy to get all existing ancillaries
        const originalPolicy = policies.find((p: any) => p._id === policyId);
        const existingAncillaries = originalPolicy?.ancillaries || [];
        const ancillaryIndex = nestedRecord.ancillaryIndex;

        // Build the updated ancillary data
        const ancillaryData = {
          ...nestedRecord,
          ...updateData,
        };
        // Remove the custom tracking fields
        delete ancillaryData.type;
        delete ancillaryData.parentId;
        delete ancillaryData.nestedType;
        delete ancillaryData.ancillaryIndex;
        delete ancillaryData.__typename;
        // Remove fake _id if it's a generated one (like 'ancillary-xxx-0')
        if (ancillaryData._id && ancillaryData._id.startsWith('ancillary-')) {
          delete ancillaryData._id;
        }

        // Rebuild the full ancillaries array with the updated item
        const updatedAncillaries = existingAncillaries.map((anc: any, idx: number) => {
          if (idx === ancillaryIndex) {
            // This is the one being updated
            const updatedAnc = { ...anc, ...ancillaryData };
            delete updatedAnc.__typename;
            return updatedAnc;
          }
          // Keep existing ancillary, just remove __typename
          const cleanAnc = { ...anc };
          delete cleanAnc.__typename;
          return cleanAnc;
        });
        
        payload.ancillaries = updatedAncillaries;
      } else {
        // Top-level update (e.g., vendorName)
        payload = updateData;
      }

      await updatePolicyData(policyId, payload);
      message.success('Policy updated successfully');
      refetch?.();
    } catch (err: any) {
      message.error(err.message || 'Failed to update policy');
    }
  };

  const handleDeletePolicy = async (policyId: string) => {
    const success = await deletePolicyById(policyId);
    if (success) {
      message.success('Policy deleted successfully');
      refetch?.();
    }
  };

  return (
    <div style={{ width: '100%' }}>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-end' }}>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => refetch?.()}
            loading={loading}
          >
            Refresh
          </Button>
          <ExportPoliciesButton data={filteredPolicies} loading={loading} />
        </Space>
      </div>
      <PoliciesTable
        data={policies}
        loading={loading}
        error={error}
        onUpdate={handleUpdatePolicy}
        onDelete={handleDeletePolicy}
        onFilteredDataChange={setFilteredPolicies}
      />
    </div>
  );
};

export default PoliciesTableContainer;
